import { CalendarClock, Layers, ListChecks, MessageSquareText, Trophy } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BeheerderShell } from "@/components/BeheerderShell";
import { Navigatiekaart } from "@/components/Navigatiekaart";
import { SkeletonLine } from "@/components/ui/skeleton";
import { countLabel } from "@/lib/counts";
import {
  useFuncties,
  useStamKlassen,
  useStamNiveaus,
  useStandaardSpeelschemas,
} from "@/features/stamgegevens/useStamgegevens";
import { useTextPresets } from "@/features/standaardteksten/useTextPresets";

/** The count under a card, or a bar of its height while it is still on its way. */
const line = (value: number | undefined, label: (count: number) => string) =>
  countLabel(value, label) ?? <SkeletonLine />;

/**
 * A landing page rather than five stacked lists: they are edited on different occasions, and
 * stacked, the bezetting grid sat below the fold of whatever you came for.
 */
export function StamgegevensPage() {
  const { t } = useTranslation(["paginas", "navigatie"]);
  const { data: functies } = useFuncties();
  const { data: klassen } = useStamKlassen();
  const { data: niveaus } = useStamNiveaus();
  const { data: speelschemas } = useStandaardSpeelschemas();
  const { data: presets } = useTextPresets();

  return (
    <BeheerderShell
      titel={t("navigatie:paginas.stamgegevens.label")}
      uitleg={t("stamgegevens.uitleg")}
    >
      {/* Three across at most, so a card keeps a readable width - the fourth and fifth wrap
          under the first two rather than squeezing all five onto one row. */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Navigatiekaart
          to="/stamgegevens/functies"
          icon={ListChecks}
          titel={t("navigatie:paginas.functies.label")}
          omschrijving={t("stamgegevens.kaarten.functies")}
          regel={line(functies?.length, (count) => t("stamgegevens.tellingen.functies", { count }))}
        />
        <Navigatiekaart
          to="/stamgegevens/klassen"
          icon={Layers}
          titel={t("navigatie:paginas.klassen.label")}
          omschrijving={t("stamgegevens.kaarten.klassen")}
          regel={line(klassen?.length, (count) => t("stamgegevens.tellingen.klassen", { count }))}
        />
        <Navigatiekaart
          to="/stamgegevens/niveaus"
          icon={Trophy}
          titel={t("navigatie:paginas.niveaus.label")}
          omschrijving={t("stamgegevens.kaarten.niveaus")}
          regel={line(niveaus?.length, (count) => t("stamgegevens.tellingen.niveaus", { count }))}
        />
        <Navigatiekaart
          to="/stamgegevens/speelschemas"
          icon={CalendarClock}
          titel={t("navigatie:paginas.speelschemas.label")}
          omschrijving={t("stamgegevens.kaarten.speelschemas")}
          regel={line(speelschemas?.length, (count) =>
            t("stamgegevens.tellingen.speelschemas", { count }),
          )}
        />
        <Navigatiekaart
          to="/stamgegevens/standaardteksten"
          icon={MessageSquareText}
          titel={t("navigatie:paginas.standaardteksten.label")}
          omschrijving={t("stamgegevens.kaarten.standaardteksten")}
          regel={line(presets?.length, (count) =>
            t("stamgegevens.tellingen.standaardteksten", { count }),
          )}
        />
      </div>
    </BeheerderShell>
  );
}
